/**
 * Generative-recipe wire codec: a batch layer over [LineWireCodec] that ships *regenerable* sub-closures as a compact
 * seed instead of their fully-expanded [LinkPreimage] records (see roadmap-iteration ledger [L11]).
 *
 * Each [LinkPreimage] on the wire is three content-addressed hashes (~135 B) regardless of what it means, so a post
 * whose body is an N-byte octet stream costs ~N such records. But that closure is a deterministic function of N raw
 * bytes: any node can rebuild the identical Link topology (and therefore the identical projections) from the bytes via
 * [Stream.OctetStream]. So instead of the expanded records we transmit a recipe line — seed + generator + head
 * projection — and the receiver regenerates the closure locally and **verifies it against the head projection**. A
 * forged seed simply fails to reproduce the head, so the dependent commitment stays unresolved: the recipe is
 * self-verifying exactly as a raw preimage is, and extends no trust.
 *
 * Two generators cover the regular structure in the data model:
 * - `OCT|head|radix|bytes` — an octet [Stream.FixedWidthGrouping] (`OctetStream.encode` for radix 2, `encodeDirect`
 *   for radix 256); this collapses post bodies and fixed key/digest blobs.
 * - `INT|head|value` — a Peano integer chain ([Quantity]); standalone economic quantities (M6/M8 price/issuance) are
 *   unary, so a large value would otherwise be O(value) records. Integer chains *inside* an octet closure ride along
 *   with the OCT recipe and need no separate line.
 *
 * Everything else (commitment spine, signatures) is irregular and ships unchanged through [LineWireCodec]. Projection
 * identity, the store, and on-disk Link structure are untouched — this is purely a transport encoding.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.Stream
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.data.projectedRecords
import com.oasisfeng.invaluable.data.resolveLink
import com.oasisfeng.invaluable.identity.Codec
import com.oasisfeng.invaluable.then
import com.oasisfeng.invaluable.toIntOrNull
import kotlinx.io.bytestring.ByteString

/**
 * One payload line together with the events a receiver will decode it into.
 *
 * The pair, not the line alone, is the complete result of encoding: the wire form is what travels, and the expansion is
 * what it means. See [RecipeWireCodec.encodeLines].
 */
data class EncodedLine(val line: String, val events: List<ReplicatedEvent>)

class RecipeWireCodec(private val projector: com.oasisfeng.invaluable.data.LinkProjector) {

    private val scheme = projector.scheme
    private val lines = LineWireCodec(scheme)

    /**
     * Encode an event batch, replacing each regenerable sub-closure with one recipe line.
     *
     * [resolver] materializes endpoints that fall outside [events] (e.g. shared integers a peer already holds, omitted
     * from a delta), so an octet body is still detected and compacted even when only its content-specific suffix is in
     * the batch. For a full-state export the batch is self-contained and the resolver is never consulted.
     */
    fun encode(events: Iterable<ReplicatedEvent>, resolver: ProjectedLinkRecordResolver): List<String> =
        encodeLines(events, resolver).map(EncodedLine::line)

    /**
     * [encode], keeping what each line expands to.
     *
     * The expansion is a *by-product* of encoding, not extra work: a recipe line is emitted precisely because its
     * regenerated records were rebuilt and matched against the head, and every other line carries exactly the one event
     * it was written from. Discarding that and recovering it with [decodeLine] — which is what a caller needing per-line
     * event counts had to do — re-runs the whole regeneration over the codec's own output. On a full-state export that
     * was a third of the encode.
     *
     * The declared expansion must equal what [decodeLine] yields for the same line, since the receiver only ever sees
     * the latter; `RecipeWireCodecTest.aDeclaredExpansionIsWhatTheDecoderYields` holds that as a property.
     */
    fun encodeLines(events: Iterable<ReplicatedEvent>, resolver: ProjectedLinkRecordResolver): List<EncodedLine> {
        val list = events.toList()
        val records = list.filterIsInstance<LinkPreimage>().map { it.record }
        val rest = list.filterNot { it is LinkPreimage }
        val links = materialize(records, resolver)

        val coverage = HashSet<LinkProjection>()
        val recipeLines = ArrayList<EncodedLine>()

        // 1) Octet-stream recipes. Records are deps-first, so a grouping head is visited after its internal nodes; once
        //    a head is found its whole closure is marked covered (and never re-detected, internal nodes aren't heads).
        for (record in records) {
            if (record.projection in coverage) continue
            val link = links[record.projection]?.let { it as? NestedLink } ?: continue
            val grouping = Stream.FixedWidthGrouping.render(link) ?: continue
            if (!grouping.framing.exactly(OCTET_CARDINALITY)) continue
            val radix = grouping.framing.radix.value
            val bytes = Stream.OctetStream.render(link)?.toByteArray() ?: continue
            val regenerated = regenerateOctet(radix, bytes) ?: continue
            if (projector.project(regenerated.link) != record.projection) continue   // only compact a faithful seed
            val regeneratedRecords = projector.projectedRecords(regenerated.link)
            if (regeneratedRecords.size > ReplicatedEventWireLimits.MAX_RECIPE_EXPANDED_RECORDS) continue
            val line = encodeOctet(record.projection, radix, bytes)
            // A line past the recipe limit is one the decoder would refuse, so ship the closure expanded instead. The
            // record cap above already implies this bound; stating it here keeps that from being an accident.
            if (line.length > ReplicatedEventWireLimits.MAX_RECIPE_LINE_BYTES) continue
            regeneratedRecords.forEach { coverage += it.projection }
            recipeLines += EncodedLine(line, regeneratedRecords.map(::LinkPreimage))
        }

        // 2) Integer recipes for the remaining (standalone) Peano chains. The chain is shared, so one line per polarity
        //    (most-positive / most-negative) regenerates the whole 0..max / 0..min run.
        var maxPositive = 0
        var minNegative = 0
        for (record in records) {
            if (record.projection in coverage) continue
            val value = links[record.projection]?.toIntOrNull() ?: continue
            if (value > maxPositive) maxPositive = value
            if (value < minNegative) minNegative = value
        }
        if (maxPositive in 1..MAX_INTEGER_RECIPE_VALUE) recipeLines += emitInteger(maxPositive, coverage)
        if (minNegative in -MAX_INTEGER_RECIPE_VALUE..-1) recipeLines += emitInteger(minNegative, coverage)

        return buildList {
            addAll(recipeLines)
            for (record in records) if (record.projection !in coverage) {
                val preimage = LinkPreimage(record)
                add(EncodedLine(lines.encode(preimage), listOf(preimage)))
            }
            // attestations last, so heads resolve after their closures
            for (event in rest) add(EncodedLine(lines.encode(event), listOf(event)))
        }
    }

    /** Decode a whole wire batch (full-state import): recipe lines expand, others pass through [LineWireCodec]. */
    fun decode(wire: Iterable<String>): List<ReplicatedEvent> = wire.flatMap(::decodeLine)

    /** Decode one wire line into 0..N events: a recipe expands to its regenerated preimages, a CMT/LNK to one event. */
    fun decodeLine(raw: String): List<ReplicatedEvent> {
        if (raw.length > ReplicatedEventWireLimits.MAX_LINE_BYTES) return emptyList()
        val line = raw.trim()
        if (line.isEmpty()) return emptyList()
        return when (line.substringBefore(FIELD_SEPARATOR)) {
            OCTET_TAG -> if (line.length <= ReplicatedEventWireLimits.MAX_RECIPE_LINE_BYTES) expandOctet(line) else emptyList()
            INTEGER_TAG -> if (line.length <= ReplicatedEventWireLimits.MAX_RECIPE_LINE_BYTES) expandInteger(line) else emptyList()
            else -> lines.decode(line)?.let(::listOf) ?: emptyList()
        }
    }

    // ───── recipe (de)serialization ─────

    private fun encodeOctet(head: LinkProjection, radix: Int, bytes: ByteArray): String =
        listOf(OCTET_TAG, b64(head), radix.toString(), Codec.base64Url(bytes)).joinToString(FIELD_SEPARATOR)

    private fun encodeInteger(head: LinkProjection, value: Int): String =
        listOf(INTEGER_TAG, b64(head), value.toString()).joinToString(FIELD_SEPARATOR)

    private fun emitInteger(value: Int, coverage: MutableSet<LinkProjection>): EncodedLine {
        val link = Quantity(value).link
        val records = projector.projectedRecords(link)
        records.forEach { coverage += it.projection }
        return EncodedLine(encodeInteger(projector.project(link), value), records.map(::LinkPreimage))
    }

    private fun expandOctet(line: String): List<ReplicatedEvent> {
        val parts = line.split(FIELD_SEPARATOR)
        if (parts.size < 4) return emptyList()
        return runCatching {
            val radix = parts[2].toInt()
            val bytes = Codec.base64UrlDecode(parts[3])
            val digitsPerOctet = when (radix) {
                OCTET_CARDINALITY -> 1
                2 -> 8
                else -> return emptyList()
            }
            val estimatedRecords = bytes.size.toLong() * digitsPerOctet + radix + RECIPE_RECORD_OVERHEAD
            if (estimatedRecords > ReplicatedEventWireLimits.MAX_RECIPE_EXPANDED_RECORDS) return emptyList()
            val head = projection(parts[1])
            val grouping = regenerateOctet(radix, bytes) ?: return emptyList()
            if (projector.project(grouping.link) != head) return emptyList()   // self-verify against the claimed head
            val records = projector.projectedRecords(grouping.link)
            if (records.size > ReplicatedEventWireLimits.MAX_RECIPE_EXPANDED_RECORDS) return emptyList()
            records.map(::LinkPreimage)
        }.getOrDefault(emptyList())
    }

    private fun expandInteger(line: String): List<ReplicatedEvent> {
        val parts = line.split(FIELD_SEPARATOR)
        if (parts.size < 3) return emptyList()
        return runCatching {
            val value = parts[2].toInt()
            if (value !in -MAX_INTEGER_RECIPE_VALUE..MAX_INTEGER_RECIPE_VALUE) return emptyList()
            val head = projection(parts[1])
            val link = Quantity(value).link
            if (projector.project(link) != head) return emptyList()
            val records = projector.projectedRecords(link)
            if (records.size > ReplicatedEventWireLimits.MAX_RECIPE_EXPANDED_RECORDS) return emptyList()
            records.map(::LinkPreimage)
        }.getOrDefault(emptyList())
    }

    private fun regenerateOctet(radix: Int, bytes: ByteArray): Stream.FixedWidthGrouping? = when (radix) {
        OCTET_CARDINALITY -> Stream.OctetStream.encodeDirect(bytes)
        2 -> Stream.OctetStream.encode(bytes)
        else -> null
    }

    private fun b64(projection: LinkProjection): String = Codec.base64Url(projection.payload.toByteArray())
    private fun projection(field: String): LinkProjection = LinkProjection(scheme, ByteString(*Codec.base64UrlDecode(field)))

    /**
     * Rebuild the Link for every record, sharing one memo so the whole batch costs O(records). An endpoint missing from
     * the batch is resolved once via [resolver] (a peer-held shared node in a delta); a truly unresolvable endpoint is
     * marked dead so its referrers are skipped rather than looping.
     */
    private fun materialize(records: List<ProjectedLinkRecord>, resolver: ProjectedLinkRecordResolver): Map<LinkProjection, Link> {
        val byProjection = records.associateBy { it.projection }
        val memo = HashMap<LinkProjection, Link>()
        val dead = HashSet<LinkProjection>()
        for (root in records) {
            if (root.projection in memo || root.projection in dead) continue
            val stack = ArrayDeque<LinkProjection>()
            stack.addLast(root.projection)
            while (stack.isNotEmpty()) {
                val current = stack.last()
                if (current in memo || current in dead) { stack.removeLast(); continue }
                val record = byProjection[current]
                if (record == null) {
                    val external = (projector.resolveLink(current, resolver) as? Observation.Known)?.value
                    if (external != null) memo[current] = external else dead += current
                    stack.removeLast(); continue
                }
                val parent = record.parent
                val target = record.target
                if (parent != null && parent in dead || target != null && target in dead) { dead += current; stack.removeLast(); continue }
                if (parent != null && parent !in memo) { stack.addLast(parent); continue }
                if (target != null && target !in memo) { stack.addLast(target); continue }
                memo[current] = (parent?.let(memo::getValue)).then(target?.let(memo::getValue))
                stack.removeLast()
            }
        }
        return memo
    }

    companion object {
        private const val OCTET_TAG = "OCT"
        private const val INTEGER_TAG = "INT"
        private const val FIELD_SEPARATOR = "|"
        private const val OCTET_CARDINALITY = 256
        private const val RECIPE_RECORD_OVERHEAD = 64
        private const val MAX_INTEGER_RECIPE_VALUE = ReplicatedEventWireLimits.MAX_RECIPE_EXPANDED_RECORDS - 1
    }
}

/**
 * Hard resource limits for the replicated-event text protocol.
 *
 * Valid wire lines are ASCII, so the byte limits are also the maximum Kotlin string lengths accepted by the sync layer.
 * Recipe seeds use a tighter line budget and an independent expansion budget because a short seed can otherwise
 * allocate and persist thousands of generated [LinkPreimage] records before its claimed head is verified. Opaque
 * [SealedContent] needs the larger general budget because it carries an encrypted preimage closure in one line.
 */
object ReplicatedEventWireLimits {
    const val MAX_LINE_BYTES: Int = 1024 * 1024
    const val MAX_RECIPE_LINE_BYTES: Int = 64 * 1024
    const val MAX_RECIPE_EXPANDED_RECORDS: Int = 4096
}
